import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  Card,
  CardContent,
} from '@mui/material';
import {
  SwapHoriz as MigrarIcon,
  CheckCircle as ConcluidoIcon,
} from '@mui/icons-material';
import api from '../axios';
import ModuleHeader from '../components/ModuleHeader';

function MigracaoAlunos() {
  const [alunosPendentes, setAlunosPendentes] = useState([]);
  const [turmas, setTurmas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  // Estado para migração individual
  const [dialogOpen, setDialogOpen] = useState(false);
  const [alunoSelecionado, setAlunoSelecionado] = useState(null);
  const [turmaSelecionada, setTurmaSelecionada] = useState('');

  // Estado para migração em lote
  const [dialogLoteOpen, setDialogLoteOpen] = useState(false);
  const [turmaLote, setTurmaLote] = useState('');

  useEffect(() => {
    fetchAlunosPendentes();
    fetchTurmas();
  }, []);

  const fetchAlunosPendentes = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/alunos/pendentes-migracao');
      setAlunosPendentes(response.data.alunos || []);
    } catch (err) {
      setError('Erro ao carregar alunos pendentes');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTurmas = async () => {
    try {
      const response = await api.get('/turmas/');
      setTurmas(response.data);
    } catch (err) {
      console.error('Erro ao carregar turmas', err);
    }
  };

  const handleOpenDialog = (aluno) => {
    setAlunoSelecionado(aluno);
    setTurmaSelecionada('');
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setAlunoSelecionado(null);
    setTurmaSelecionada('');
  };

  const handleMigrarAluno = async () => {
    if (!turmaSelecionada) {
      setError('Selecione uma turma de destino');
      return;
    }

    setLoading(true);
    setError('');
    setMessage('');

    try {
      const response = await api.post(
        `/alunos/${alunoSelecionado.aluno_id}/migrar?nova_turma_id=${turmaSelecionada}`
      );
      setMessage(
        `Aluno ${response.data.aluno_nome} migrado com sucesso para ${response.data.nova_turma_nome}`
      );
      handleCloseDialog();
      fetchAlunosPendentes(); // Recarregar lista
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Erro ao migrar aluno';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialogLote = () => {
    setTurmaLote('');
    setDialogLoteOpen(true);
  };

  const handleCloseDialogLote = () => {
    setDialogLoteOpen(false);
    setTurmaLote('');
  };

  const handleMigrarEmLote = async () => {
    if (!turmaLote) {
      setError('Selecione uma turma de destino');
      return;
    }

    setLoading(true);
    setError('');
    setMessage('');

    try {
      const response = await api.post(
        `/alunos/migrar-em-lote?nova_turma_id=${turmaLote}&apenas_pendentes=true`
      );
      setMessage(
        `${response.data.total_migrados} alunos migrados com sucesso para ${response.data.nova_turma_nome}`
      );
      handleCloseDialogLote();
      fetchAlunosPendentes(); // Recarregar lista
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Erro ao migrar alunos';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 }, px: { xs: 1, md: 2 } }}>
      <ModuleHeader module="Secretaria" title="Migração de Alunos" />

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            ℹ️ Como funciona a migração
          </Typography>
          <Typography variant="body2" paragraph>
            • Alunos precisam completar <strong>8 lições únicas</strong> para serem aprovados
          </Typography>
          <Typography variant="body2" paragraph>
            • Se um aluno não completar todas as lições em uma turma/período, ele precisa ser
            migrado para continuar
          </Typography>
          <Typography variant="body2">
            • A migração mantém o histórico de frequências e permite que o aluno continue de onde
            parou
          </Typography>
        </CardContent>
      </Card>

      {message && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>
          {message}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 1, md: 2 } }}>
        <Typography variant="h6">
          Alunos Pendentes de Conclusão: {alunosPendentes.length}
        </Typography>
        <Button
          variant="contained"
          startIcon={<MigrarIcon />}
          onClick={handleOpenDialogLote}
          disabled={alunosPendentes.length === 0}
        >
          Migrar Todos em Lote
        </Button>
      </Box>

      {loading && !alunosPendentes.length ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : alunosPendentes.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <ConcluidoIcon sx={{ fontSize: 60, mb: 2 }} />
          <Typography variant="h6" color="textSecondary">
            Nenhum aluno pendente de migração
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Todos os alunos com matrículas ativas completaram as 8 lições
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Aluno</TableCell>
                <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Turma Atual</TableCell>
                <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Período</TableCell>
                <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }} align="center">
                  Progresso
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }} align="center">
                  Ações
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {alunosPendentes.map((aluno) => (
                <TableRow key={aluno.aluno_id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {aluno.aluno_nome}
                    </Typography>
                  </TableCell>
                  <TableCell>{aluno.aluno_email}</TableCell>
                  <TableCell>{aluno.turma_atual_nome}</TableCell>
                  <TableCell>{aluno.periodo_atual}</TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                      <Chip
                        label={`${aluno.licoes_completas}/8`}
                        color={aluno.licoes_completas >= 6 ? 'warning' : 'error'}
                        size="small"
                      />
                      <Typography variant="caption" color="textSecondary">
                        ({aluno.licoes_faltantes} faltando)
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<MigrarIcon />}
                      onClick={() => handleOpenDialog(aluno)}
                    >
                      Migrar
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialog para migração individual */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Migrar Aluno: {alunoSelecionado?.aluno_nome}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Lições completadas: {alunoSelecionado?.licoes_completas}/8
              <br />
              Faltam: {alunoSelecionado?.licoes_faltantes} lições
            </Alert>

            <FormControl fullWidth>
              <InputLabel>Turma de Destino</InputLabel>
              <Select
                value={turmaSelecionada}
                onChange={(e) => setTurmaSelecionada(e.target.value)}
                label="Turma de Destino"
              >
                {turmas.map((turma) => (
                  <MenuItem key={turma.id} value={turma.id}>
                    {turma.nome} - {turma.periodo?.mes}/{turma.periodo?.ano}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button
            onClick={handleMigrarAluno}
            variant="contained"
            disabled={!turmaSelecionada || loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Confirmar Migração'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para migração em lote */}
      <Dialog open={dialogLoteOpen} onClose={handleCloseDialogLote} maxWidth="sm" fullWidth>
        <DialogTitle>Migrar Todos os Alunos Pendentes</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Esta ação irá migrar <strong>{alunosPendentes.length} alunos</strong> que não
              completaram as 8 lições para a turma selecionada.
            </Alert>

            <FormControl fullWidth>
              <InputLabel>Turma de Destino</InputLabel>
              <Select
                value={turmaLote}
                onChange={(e) => setTurmaLote(e.target.value)}
                label="Turma de Destino"
              >
                {turmas.map((turma) => (
                  <MenuItem key={turma.id} value={turma.id}>
                    {turma.nome} - {turma.periodo?.mes}/{turma.periodo?.ano}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogLote}>Cancelar</Button>
          <Button
            onClick={handleMigrarEmLote}
            variant="contained"
            color="warning"
            disabled={!turmaLote || loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Confirmar Migração em Lote'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default MigracaoAlunos;
